import React, { useEffect, useState } from 'react';
import { ChevronRight, Check, Sparkles, Crown, Shield, Lock, User, Bell, Trash2, AlertTriangle, Save, Mail, Settings } from 'lucide-react';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { ChangeEmailModal } from '../components/Settings/ChangeEmailModal';
import { TwoFactorModal } from '../components/Settings/TwoFactorModal';
import { PrivacySettingsModal } from '../components/Settings/PrivacySettingsModal';
import { SubscriptionModal } from '../components/Settings/SubscriptionModal';
import { DeleteAccountModal } from '../components/Settings/DeleteAccountModal';
import { BillingManagementModal } from '../components/Settings/BillingManagementModal';
import { CancelSubscriptionModal } from '../components/Settings/CancelSubscriptionModal';
import { deleteUserAccount } from '../services/accountService';
import { supabase } from '../lib/supabaseClient';
export function SettingsPage() {
  const navigate = useNavigate();
  const {
    user,
    signOut,
    updateUserProfile
  } = useAuth();
  const {
    showToast
  } = useToast();
  const [formData, setFormData] = useState({
    firstName: user?.user_metadata?.firstName || '',
    lastName: user?.user_metadata?.lastName || '',
    email: user?.email || ''
  });
  const [emailNotifications, setEmailNotifications] = useState(user?.user_metadata?.emailNotifications ?? true);
  const [pushNotifications, setPushNotifications] = useState(user?.user_metadata?.pushNotifications ?? true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [profile, setProfile] = useState<{
    is_pro: boolean;
    scan_credits: number;
  } | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const currentPlan = user?.user_metadata?.subscriptionPlan || 'free';
  useEffect(() => {
    async function fetchProfile() {
      if (!user?.id) return;
      try {
        const {
          data,
          error
        } = await supabase.from('profiles').select('is_pro, scan_credits').eq('id', user.id).single();
        if (error) throw error;
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } finally {
        setLoadingProfile(false);
      }
    }
    fetchProfile();
    const channel = supabase.channel('profile-settings-updates').on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'profiles',
      filter: `id=eq.${user?.id}`
    }, payload => {
      const newProfile = payload.new as {
        is_pro: boolean;
        scan_credits: number;
      };
      setProfile(newProfile);
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);
  const isPro = profile?.is_pro || false;
  const scansCount = profile?.scan_credits || 0;
  const userTier = isPro ? 'pro' : scansCount > 0 ? 'active' : 'free';
  const handleSaveChanges = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await updateUserProfile({
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      setSuccess('Profile updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  const handleNotificationChange = async (type: 'email' | 'push', value: boolean) => {
    setError(null);
    setSuccess(null);
    if (type === 'email') {
      setEmailNotifications(value);
    } else {
      setPushNotifications(value);
    }
    try {
      await updateUserProfile({
        emailNotifications: type === 'email' ? value : emailNotifications,
        pushNotifications: type === 'push' ? value : pushNotifications
      });
      setSuccess(`${type === 'email' ? 'Email' : 'Push'} notifications ${value ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      if (type === 'email') {
        setEmailNotifications(!value);
      } else {
        setPushNotifications(!value);
      }
      setError(`Failed to update ${type} notification settings`);
    }
  };
  const handleLogout = async () => {
    await signOut();
    navigate('/');
    showToast('Logged out successfully', 'success');
  };
  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeleteLoading(true);
    setError(null);
    try {
      await deleteUserAccount(user.id);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
      setShowDeleteAccountModal(false);
    } finally {
      setDeleteLoading(false);
    }
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-10174">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-10175" />

      <WebsiteHeader data-id="element-10176" />

      <div className="max-w-3xl mx-auto w-full relative z-10" data-id="element-10177">
        <main className="px-6 pt-12 pb-24" data-id="element-10178">
          {/* Page Header */}
          <div className="mb-12" data-id="element-10179">
            <div className="inline-flex items-center gap-2 mb-4 bg-[#FF0000]/10 border border-[#FF0000]/30 px-3 py-1" data-id="element-10180">
              <Settings size={14} className="text-[#FF0000]" data-id="element-10181" />
              <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-10182">
                System Configuration
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-2 uppercase tracking-tight" data-id="element-10183">
              Settings & Preferences
            </h1>
            <p className="text-gray-400 font-mono text-sm max-w-xl" data-id="element-10184">
              Manage your account parameters, security protocols, and
              notification preferences.
            </p>
          </div>

          {/* Success/Error Messages */}
          {success && <div className="bg-green-500/10 border border-green-500/30 p-4 mb-8 flex items-center gap-3" data-id="element-10185">
              <Check className="text-green-500 flex-shrink-0" size={20} data-id="element-10186" />
              <p className="text-green-500 text-sm font-mono uppercase tracking-wide" data-id="element-10187">
                {success}
              </p>
            </div>}
          {error && <div className="bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 mb-8 flex items-center gap-3" data-id="element-10188">
              <AlertTriangle className="text-[#FF0000] flex-shrink-0" size={20} data-id="element-10189" />
              <p className="text-[#FF0000] text-sm font-mono uppercase tracking-wide" data-id="element-10190">
                {error}
              </p>
            </div>}

          {/* Subscription Card */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-10191">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10192" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10193" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10194" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10195" />

            <div className="flex items-start gap-4 mb-6" data-id="element-10196">
              <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center border ${userTier === 'pro' ? 'bg-[#FFD700]/10 border-[#FFD700] text-[#FFD700]' : userTier === 'active' ? 'bg-[#FF0000]/10 border-[#FF0000] text-[#FF0000]' : 'bg-gray-900 border-gray-700 text-gray-500'}`} data-id="element-10197">
                {userTier === 'pro' || userTier === 'active' ? <Crown size={24} data-id="element-10198" /> : <Sparkles size={24} data-id="element-10199" />}
              </div>
              <div className="flex-1 min-w-0" data-id="element-10200">
                <h3 className="font-bold text-white text-lg uppercase tracking-wide mb-1" data-id="element-10201">
                  {loadingProfile ? 'Loading...' : userTier === 'pro' ? 'Pro Plan Active' : userTier === 'active' ? 'Pay Per Scan' : 'Free Plan'}
                </h3>
                <p className="text-sm text-gray-400 font-mono" data-id="element-10202">
                  {loadingProfile ? 'Verifying clearance...' : userTier === 'pro' ? 'Unlimited access enabled' : userTier === 'active' ? `${scansCount} scan${scansCount !== 1 ? 's' : ''} remaining` : 'Limited features available'}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-10203">
              <button onClick={() => setShowSubscriptionModal(true)} className="flex-1 bg-[#FF0000] text-white py-3 font-bold hover:bg-[#FF0000]/90 transition-all text-xs uppercase tracking-widest shadow-[0_0_15px_rgba(255,0,0,0.3)]" data-id="element-10204">
                {userTier === 'pro' ? 'View Plan Details' : 'Upgrade Plan'}
              </button>
              {(userTier === 'pro' || userTier === 'active') && <button onClick={() => setShowBillingModal(true)} className="flex-1 bg-transparent border border-gray-700 text-gray-300 py-3 font-bold hover:border-gray-500 hover:text-white transition-all text-xs uppercase tracking-widest" data-id="element-10205">
                  Billing History
                </button>}
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-10206">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10207" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10208" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10209" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10210" />

            <div className="flex items-center gap-3 mb-6" data-id="element-10211">
              <User size={20} className="text-[#FF0000]" data-id="element-10212" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-10213">
                Identity Parameters
              </h3>
            </div>

            <form onSubmit={handleSaveChanges} className="space-y-6" data-id="element-10214">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10215">
                <div data-id="element-10216">
                  <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-10217">
                    First Name
                  </label>
                  <input type="text" value={formData.firstName} onChange={e => setFormData({
                  ...formData,
                  firstName: e.target.value
                })} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white transition-all font-mono text-sm" data-id="element-10218" />
                </div>
                <div data-id="element-10219">
                  <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-10220">
                    Last Name
                  </label>
                  <input type="text" value={formData.lastName} onChange={e => setFormData({
                  ...formData,
                  lastName: e.target.value
                })} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white transition-all font-mono text-sm" data-id="element-10221" />
                </div>
              </div>

              <div data-id="element-10222">
                <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-10223">
                  Email Address
                </label>
                <div className="flex gap-3" data-id="element-10224">
                  <div className="flex-1 px-4 py-3 bg-[#050505] border border-gray-800 text-gray-400 font-mono text-sm flex items-center" data-id="element-10225">
                    <Mail size={14} className="mr-3 text-gray-600" data-id="element-10226" />
                    {user?.email || ''}
                  </div>
                  <button type="button" onClick={() => setShowChangeEmailModal(true)} className="px-6 py-3 bg-gray-900 border border-gray-700 text-white font-bold hover:bg-gray-800 hover:border-gray-500 transition-all text-xs uppercase tracking-widest whitespace-nowrap" data-id="element-10227">
                    Change
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-800" data-id="element-10228">
                <button type="submit" disabled={loading} className="w-full bg-white text-black py-3 font-bold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-10229">
                  {loading ? 'Processing Update...' : <>
                      <Save size={16} data-id="element-10230" />
                      Save Changes
                    </>}
                </button>
              </div>
            </form>
          </div>

          {/* Notifications */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-10231">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10232" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10233" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10234" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10235" />

            <div className="flex items-center gap-3 mb-6" data-id="element-10236">
              <Bell size={20} className="text-[#FF0000]" data-id="element-10237" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-10238">
                Alert Protocols
              </h3>
            </div>

            <div className="space-y-6" data-id="element-10239">
              <div className="flex items-center justify-between" data-id="element-10240">
                <div data-id="element-10241">
                  <p className="font-bold text-white text-sm uppercase tracking-wide" data-id="element-10242">
                    Email Reports
                  </p>
                  <p className="text-xs text-gray-500 font-mono mt-1" data-id="element-10243">
                    Receive detailed analysis via secure email
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-10244">
                  <input type="checkbox" checked={emailNotifications} onChange={e => handleNotificationChange('email', e.target.checked)} className="sr-only peer" data-id="element-10245" />
                  <div className="w-11 h-6 bg-gray-900 border border-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-500 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF0000]/20 peer-checked:border-[#FF0000] peer-checked:after:bg-[#FF0000] peer-checked:after:border-[#FF0000]" data-id="element-10246"></div>
                </label>
              </div>

              <div className="h-px bg-gray-900" data-id="element-10247" />

              <div className="flex items-center justify-between" data-id="element-10248">
                <div data-id="element-10249">
                  <p className="font-bold text-white text-sm uppercase tracking-wide" data-id="element-10250">
                    System Alerts
                  </p>
                  <p className="text-xs text-gray-500 font-mono mt-1" data-id="element-10251">
                    Real-time notifications for completed scans
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-10252">
                  <input type="checkbox" checked={pushNotifications} onChange={e => handleNotificationChange('push', e.target.checked)} className="sr-only peer" data-id="element-10253" />
                  <div className="w-11 h-6 bg-gray-900 border border-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-500 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF0000]/20 peer-checked:border-[#FF0000] peer-checked:after:bg-[#FF0000] peer-checked:after:border-[#FF0000]" data-id="element-10254"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-10255">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10256" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10257" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10258" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10259" />

            <div className="flex items-center gap-3 mb-6" data-id="element-10260">
              <Shield size={20} className="text-[#FF0000]" data-id="element-10261" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-10262">
                Security Protocols
              </h3>
            </div>

            <div className="space-y-4" data-id="element-10263">
              <button onClick={() => setShowTwoFactorModal(true)} className="w-full flex items-center justify-between p-4 bg-[#050505] border border-gray-800 hover:border-gray-600 transition-all group/btn" data-id="element-10264">
                <div className="flex items-center gap-4" data-id="element-10265">
                  <div className="p-2 bg-gray-900 border border-gray-800 group-hover/btn:border-gray-600 transition-colors" data-id="element-10266">
                    <Lock size={16} className="text-gray-400" data-id="element-10267" />
                  </div>
                  <span className="font-bold text-gray-300 text-sm uppercase tracking-wide group-hover/btn:text-white" data-id="element-10268">
                    Two-Factor Authentication
                  </span>
                </div>
                <ChevronRight size={16} className="text-gray-600 group-hover/btn:text-[#FF0000] transition-colors" data-id="element-10269" />
              </button>

              <button onClick={() => setShowPrivacyModal(true)} className="w-full flex items-center justify-between p-4 bg-[#050505] border border-gray-800 hover:border-gray-600 transition-all group/btn" data-id="element-10270">
                <div className="flex items-center gap-4" data-id="element-10271">
                  <div className="p-2 bg-gray-900 border border-gray-800 group-hover/btn:border-gray-600 transition-colors" data-id="element-10272">
                    <Shield size={16} className="text-gray-400" data-id="element-10273" />
                  </div>
                  <span className="font-bold text-gray-300 text-sm uppercase tracking-wide group-hover/btn:text-white" data-id="element-10274">
                    Privacy Configuration
                  </span>
                </div>
                <ChevronRight size={16} className="text-gray-600 group-hover/btn:text-[#FF0000] transition-colors" data-id="element-10275" />
              </button>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-[#FF0000]/5 border border-[#FF0000]/30 p-8 mb-8 relative" data-id="element-10276">
            <h3 className="font-bold text-[#FF0000] mb-4 flex items-center gap-2 uppercase tracking-wider text-sm" data-id="element-10277">
              <AlertTriangle size={16} data-id="element-10278" />
              Critical Zone
            </h3>
            <p className="text-xs text-gray-400 mb-6 font-mono" data-id="element-10279">
              WARNING: Account deletion is irreversible. All forensic data and
              analysis logs will be permanently purged.
            </p>
            <button onClick={() => setShowDeleteAccountModal(true)} className="w-full bg-transparent border border-[#FF0000] text-[#FF0000] py-3 font-bold hover:bg-[#FF0000] hover:text-white transition-all text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-10280">
              <Trash2 size={16} data-id="element-10281" />
              Delete Account
            </button>
          </div>

          {/* Logout Button */}
          <button onClick={handleLogout} className="w-full bg-gray-900 border border-gray-800 text-gray-400 py-4 font-bold hover:bg-gray-800 hover:text-white hover:border-gray-600 transition-all text-xs uppercase tracking-widest" data-id="element-10282">
            Terminate Session
          </button>
        </main>
      </div>

      {/* Modals */}
      <ChangeEmailModal isOpen={showChangeEmailModal} onClose={() => setShowChangeEmailModal(false)} data-id="element-10283" />
      <TwoFactorModal isOpen={showTwoFactorModal} onClose={() => setShowTwoFactorModal(false)} data-id="element-10284" />
      <PrivacySettingsModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} data-id="element-10285" />
      <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} currentPlan={currentPlan} data-id="element-10286" />
      <DeleteAccountModal isOpen={showDeleteAccountModal} onClose={() => setShowDeleteAccountModal(false)} onConfirm={handleDeleteAccount} loading={deleteLoading} data-id="element-10287" />
      <BillingManagementModal isOpen={showBillingModal} onClose={() => setShowBillingModal(false)} currentPlan={currentPlan as 'pay-per-scan' | 'pro'} data-id="element-10288" />
      <CancelSubscriptionModal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} currentPlan={currentPlan as 'pay-per-scan' | 'pro'} data-id="element-10289" />
    </div>;
}